import {engineConfig, type Dispatch} from '@packages/systems/flux';
import {
  type RemoteSandboxActions,
  codeDependenciesInstallationFinished,
  codeBuildSucceeded,
  codeBuildStarted,
  remoteSandboxCreated,
  remoteSandboxExpired,
  remoteSandboxFilesDeleted,
  remoteSandboxInitiated,
  remoteSandboxSyncCompleted,
  remoteSandboxSyncStarted,
  codeBuildFailed,
  remoteFileSystemFilesCommitted,
} from './actionCreators';
import {allSynced, selectRemoteSandbox} from './CodeLibrariesSlice';
import type {RemoteSandboxState} from './RemoteSandboxStore';
import {CreateSandboxResponse} from '@packages/domains/code-libraries/data-types';
import fetch from '@packages/utilities/fetch';
import {filterMap} from '@packages/utilities/array';
import {getDesignerSessionId} from '@packages/systems/multiplayer/session';
import {Result} from '@packages/systems/wfdl';
import {
  PROJECTED_INDEX_FILENAME,
  SANDBOX_WORKING_DIR,
  buildCodeLibrary,
  isComponentFilename,
  fromComponentFilename,
  hashProjectedSourceForStamp,
  stripLeadingSlash,
  withFileChangeTracking,
  type BuildMeta,
  type BuildPhase,
} from '@packages/systems/code-libraries/core';
import {getSandboxClientFromSession, isSessionExpired} from './sandboxClient';
import {
  computeBuildInputsHash,
  createBuildArtifactCache,
} from './buildArtifactCache';
import {createTabFocusObserver} from './tabFocusObserver';
import {consumePasteHarnessApplied} from '../startFromCode/pasteHarnessBuildMarker';
import type {SandboxInstance} from '@blaxel/core';
import {telemetry} from '@packages/systems/designer-telemetry';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {z} from 'zod';

const BUILD_PHASE_ORDER: readonly BuildPhase[] = [
  'deps_install',
  'typecheck',
  'vite_build',
  'fs_read',
];

/**
 * Phases driven by `sandbox.process.exec` (which exposes `startedAt` /
 * `completedAt`). `fs_read` is intentionally excluded — it uses `sandbox.fs.*`
 * and has no ProcessResponse to read sandbox-side timestamps from, so its
 * server duration will never be present and shouldn't count against
 * `serverTimingComplete`.
 */
const EXEC_BACKED_PHASES: readonly BuildPhase[] = [
  'deps_install',
  'typecheck',
  'vite_build',
];

/**
 * A sandbox-reported phase duration is treated as corrupt when it exceeds the
 * client-measured duration for the same `exec` by more than this. The client
 * wraps that exec, so it can only be longer (a background tab delays promise
 * resolution); a server duration this far ahead of the client is a bad Blaxel
 * timestamp, not a slow build.
 */
const MAX_SERVER_CLIENT_TIMING_DIVERGENCE_MS = 30_000;

type SandboxDirectory = Awaited<ReturnType<SandboxInstance['fs']['ls']>>;
type FileToSync = {path: string; content: string};

const sandboxPathToFilesystemPath = (path: string): string => {
  if (path === SANDBOX_WORKING_DIR) {
    return '/';
  }

  if (path.startsWith(`${SANDBOX_WORKING_DIR}/`)) {
    return path.slice(SANDBOX_WORKING_DIR.length);
  }

  return path.startsWith('/') ? path : `/${path}`;
};

const dirname = (path: string): string => {
  const lastSlashIndex = path.lastIndexOf('/');
  return lastSlashIndex <= 0 ? '/' : path.slice(0, lastSlashIndex);
};

const verifiedFilesFromDirectory = (
  directory: SandboxDirectory,
  requestedPaths: Set<string>
): Set<string> => {
  const verified = new Set<string>();

  for (const file of directory.files) {
    const path = sandboxPathToFilesystemPath(file.path);
    if (requestedPaths.has(path)) {
      verified.add(path);
    }
  }

  return verified;
};

const verifySyncedPaths = async (
  sandboxClient: SandboxInstance,
  filesToSync: FileToSync[],
  writeResponse: SandboxDirectory | undefined
): Promise<string[]> => {
  const requestedPaths = new Set(filesToSync.map((file) => file.path));
  const verifiedPaths = writeResponse
    ? verifiedFilesFromDirectory(writeResponse, requestedPaths)
    : new Set<string>();

  const unverifiedPaths = [...requestedPaths].filter(
    (path) => !verifiedPaths.has(path)
  );
  const parentDirectories = new Set(unverifiedPaths.map(dirname));

  await Promise.all(
    [...parentDirectories].map(async (parentDirectory) => {
      let directory: SandboxDirectory;
      const sandboxDirectory =
        parentDirectory === '/'
          ? SANDBOX_WORKING_DIR
          : `${SANDBOX_WORKING_DIR}${parentDirectory}`;

      try {
        directory = await sandboxClient.fs.ls(sandboxDirectory);
      } catch (error) {
        reportBlaxelSandboxError(error, 'remoteSandboxEngine.verifyLs');
        console.warn(
          '[RemoteSandboxEngine] Unable to verify sandbox directory writes',
          {
            directory: sandboxDirectory,
            error,
          }
        );
        return;
      }

      for (const path of verifiedFilesFromDirectory(
        directory,
        requestedPaths
      )) {
        verifiedPaths.add(path);
      }
    })
  );

  return [...verifiedPaths];
};

/**
 * Confirms that files we asked Blaxel to remove are actually gone. `fs.rm` can
 * resolve without the file having been removed, which would silently leave a
 * stale file on the sandbox: its entry would be dropped from state while the
 * file lingers, so it never gets removed. Re-lists the parent directories and
 * returns only the paths verifiably absent. A path still present — or whose
 * parent directory could not be listed — is excluded so the caller leaves it
 * PENDING_DELETE for a later retry. Unlike the write path, where verification
 * only feeds telemetry, this result gates dropping the entry from state, so an
 * `ls` failure must fail closed: treating it as "removed" would abandon the
 * file with no retry path.
 */
const verifyRemovedPaths = async (
  sandboxClient: SandboxInstance,
  removedPaths: string[]
): Promise<string[]> => {
  const requestedPaths = new Set(removedPaths);
  const stillPresent = new Set<string>();
  const listedDirectories = new Set<string>();
  const parentDirectories = new Set(removedPaths.map(dirname));

  await Promise.all(
    [...parentDirectories].map(async (parentDirectory) => {
      const sandboxDirectory =
        parentDirectory === '/'
          ? SANDBOX_WORKING_DIR
          : `${SANDBOX_WORKING_DIR}${parentDirectory}`;

      try {
        const directory = await sandboxClient.fs.ls(sandboxDirectory);
        listedDirectories.add(parentDirectory);
        for (const path of verifiedFilesFromDirectory(
          directory,
          requestedPaths
        )) {
          stillPresent.add(path);
        }
      } catch (error) {
        reportBlaxelSandboxError(error, 'remoteSandboxEngine.verifyRemoveLs');
      }
    })
  );

  return removedPaths.filter(
    (path) => listedDirectories.has(dirname(path)) && !stillPresent.has(path)
  );
};

/**
 * EXT-865: observe-only probe for store↔sandbox divergence. Serialization fixes
 * the single-tab delete→undo race, but the per-(site, user) sandbox has other
 * unserialized writers — a second Designer tab, the AI tools' direct
 * `client.fs.write` — that can leave a SYNCED file missing from disk, fatal when
 * it's the build entry module. Returns the component-scoped files
 * (`*.webflow.tsx` + the `/index.ts` entry) that are SYNCED in the store but
 * absent on disk. Lists each in-scope path's parent directory rather than just
 * the root, because `isComponentFilename` has no root constraint and source
 * files can project into folders — a nested file checked against a root-only
 * listing would read as absent on every build. Detection only, and fails open:
 * a directory that can't be listed reports nothing for its own files.
 */
const detectDivergentSyncedPaths = async (
  sandboxClient: SandboxInstance,
  filesystem: RemoteSandboxState['filesystem']
): Promise<string[]> => {
  const syncedScopedPaths = Object.entries(filesystem)
    .filter(
      ([path, file]) =>
        file.status === 'SYNCED' &&
        (isComponentFilename(path) || path === PROJECTED_INDEX_FILENAME)
    )
    .map(([path]) => path);
  if (syncedScopedPaths.length === 0) return [];

  const presentPaths = new Set<string>();
  const listedDirectories = new Set<string>();
  const parentDirectories = new Set(syncedScopedPaths.map(dirname));

  await Promise.all(
    [...parentDirectories].map(async (parentDirectory) => {
      const sandboxDirectory =
        parentDirectory === '/'
          ? SANDBOX_WORKING_DIR
          : `${SANDBOX_WORKING_DIR}${parentDirectory}`;

      try {
        const directory = await sandboxClient.fs.ls(sandboxDirectory);
        listedDirectories.add(parentDirectory);
        for (const file of directory.files) {
          presentPaths.add(sandboxPathToFilesystemPath(file.path));
        }
      } catch (error) {
        reportBlaxelSandboxError(
          error,
          'remoteSandboxEngine.divergenceProbeLs'
        );
      }
    })
  );

  return syncedScopedPaths.filter(
    (path) => listedDirectories.has(dirname(path)) && !presentPaths.has(path)
  );
};

function phasesReached(meta: BuildMeta): readonly BuildPhase[] {
  if (meta.failurePhase === 'none') return BUILD_PHASE_ORDER;
  const failIndex = BUILD_PHASE_ORDER.indexOf(meta.failurePhase);
  return failIndex >= 0
    ? BUILD_PHASE_ORDER.slice(0, failIndex + 1)
    : BUILD_PHASE_ORDER;
}

function sumDefined(values: ReadonlyArray<number | undefined>): number | null {
  const defined = values.filter((v): v is number => v != null);
  return defined.length > 0
    ? defined.reduce((total, value) => total + value, 0)
    : null;
}

/**
 * Computes the build duration to alert on: sums sandbox-side per-phase
 * durations across every exec-backed phase that actually ran. Returns `null`
 * when any expected server duration is missing so dashboards can distinguish
 * "no signal" from "fast build".
 *
 * Blaxel timestamps are occasionally wrong rather than missing: a phase can
 * report minutes when the client measured that same exec in seconds. Those
 * values are monotonic, so `serverTimingComplete` alone can't catch them. We
 * compare each server duration against the client duration for the same phase
 * and mark the event as suspect when the sandbox clock is implausibly ahead.
 */
function deriveServerBuildDuration(meta: BuildMeta): {
  serverBuildDurationMs: number | null;
  serverTimingComplete: boolean;
  serverTimingSuspect: boolean;
  serverTimingSuspectPhases: BuildPhase[];
} {
  const reached = phasesReached(meta);
  const expectedExec = reached.filter((p) => EXEC_BACKED_PHASES.includes(p));
  const timingComplete = expectedExec.every(
    (p) => meta.serverPhaseDurations[p] != null
  );
  const serverTimingSuspectPhases = expectedExec.filter((phase) => {
    const serverDuration = meta.serverPhaseDurations[phase];
    const clientDuration = meta.phaseDurations[phase];
    return (
      serverDuration != null &&
      clientDuration != null &&
      serverDuration - clientDuration > MAX_SERVER_CLIENT_TIMING_DIVERGENCE_MS
    );
  });
  const serverTimingSuspect = serverTimingSuspectPhases.length > 0;
  // Report suspect timings as incomplete so the primary alert's
  // `serverTimingComplete:true` filter ignores them. Raw per-phase values stay
  // on the event so the bad timestamps remain inspectable.
  const serverTimingComplete = timingComplete && !serverTimingSuspect;
  const serverBuildDurationMs = serverTimingComplete
    ? sumDefined(expectedExec.map((p) => meta.serverPhaseDurations[p]))
    : null;
  return {
    serverBuildDurationMs,
    serverTimingComplete,
    serverTimingSuspect,
    serverTimingSuspectPhases,
  };
}

const getSandboxSession = async (
  siteShortName: string
): Promise<CreateSandboxResponse | null> => {
  const response = await fetch(`/api/code-libraries/${siteShortName}/sandbox`, {
    method: 'POST',
  });
  const json = await response.json();
  const parsed = CreateSandboxResponse.parse(json);

  return parsed;
};

/**
 * Blaxel's SDK returns structured platform errors for sandbox routing and
 * availability issues. We only recreate the sandbox for certain error codes.
 */
const BlaxelErrorCodeSchema = z.enum([
  'ROUTE_NOT_FOUND',
  'WORKLOAD_NOT_FOUND',
  'WORKSPACE_NOT_FOUND',
  'WORKLOAD_UNAVAILABLE',
  'AUTHENTICATION_REQUIRED',
  'AUTHENTICATION_FAILED',
  'FORBIDDEN',
  'BAD_REQUEST',
  'USAGE_LIMIT_EXCEEDED',
  'POLICY_VIOLATION',
]);
type BlaxelErrorCode = z.infer<typeof BlaxelErrorCodeSchema>;

const RETRYABLE_BLAXEL_ERRORS: readonly BlaxelErrorCode[] = [
  // Sandbox was garbage collected. Create a new sandbox.
  BlaxelErrorCodeSchema.enum.WORKLOAD_UNAVAILABLE,
  BlaxelErrorCodeSchema.enum.WORKLOAD_NOT_FOUND,
  // Preview URL is no longer valid. Create a new session to get a new preview URL.
  BlaxelErrorCodeSchema.enum.ROUTE_NOT_FOUND,
  // Session is no longer valid. Create a new session to get a new session token.
  BlaxelErrorCodeSchema.enum.AUTHENTICATION_FAILED,
];

const BlaxelErrorPayloadSchema = z
  .object({
    code: BlaxelErrorCodeSchema,
    origin: z.literal('platform'),
  })
  .passthrough();

const BlaxelErrorContainerSchema = z
  .object({
    error: BlaxelErrorPayloadSchema,
  })
  .passthrough();

const BlaxelErrorDetailsSchema = z
  .object({
    data: BlaxelErrorContainerSchema.optional(),
    error: BlaxelErrorContainerSchema.optional(),
    response: z
      .object({
        status: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()
  .transform((errorDetails) => ({
    code:
      errorDetails.data?.error?.code ?? errorDetails.error?.error?.code ?? null,
    origin:
      errorDetails.data?.error?.origin ??
      errorDetails.error?.error?.origin ??
      null,
    status: errorDetails.response?.status ?? null,
  }));

type NormalizedBlaxelSandboxError = {
  code: BlaxelErrorCode | null;
  origin: string | null;
  status: number | null;
  fallbackReason: 'failed_to_fetch' | 'http_502_503' | null;
  isSandboxUnavailableError: boolean;
  hasStructuredDetails: boolean;
  message: string;
};

const normalizeBlaxelSandboxError = (
  error: unknown
): NormalizedBlaxelSandboxError => {
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return {
      code: null,
      origin: null,
      status: null,
      fallbackReason: 'failed_to_fetch',
      isSandboxUnavailableError: true,
      hasStructuredDetails: false,
      message: error.message,
    };
  }

  const parsedError = BlaxelErrorDetailsSchema.safeParse(error);
  const details = parsedError.success
    ? parsedError.data
    : {
        code: null,
        origin: null,
        status: null,
      };
  const hasStructuredDetails = Boolean(details.code || details.origin);

  if (!hasStructuredDetails) {
    const isSandboxUnavailableError =
      details.status === 502 || details.status === 503;

    return {
      ...details,
      fallbackReason: isSandboxUnavailableError ? 'http_502_503' : null,
      isSandboxUnavailableError,
      hasStructuredDetails,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  return {
    ...details,
    fallbackReason: null,
    isSandboxUnavailableError:
      details.code != null && RETRYABLE_BLAXEL_ERRORS.includes(details.code),
    hasStructuredDetails,
    message: error instanceof Error ? error.message : String(error),
  };
};

export const reportBlaxelSandboxError = (
  error: unknown,
  context: string,
  extra?: Record<string, string | number | boolean | null>
): void => {
  const details = normalizeBlaxelSandboxError(error);

  telemetry.sendEvent('datadog::blaxelSandboxError', {
    context,
    ...details,
    ...extra,
  });
};

/**
 * Records an expected sandbox session-expiry short-circuit. This is NOT an
 * error — the session simply crossed the expiry buffer — so it emits its own
 * neutral event instead of going through `reportBlaxelSandboxError` (the error
 * stream). Lets us see how often, and for which caller, a sandbox op was
 * skipped because the session was expired.
 */
export const reportBlaxelSandboxSessionExpired = (
  context: string,
  extra?: Record<string, string | number | boolean | null>
): void => {
  telemetry.sendEvent('datadog::blaxelSandboxSessionExpired', {
    context,
    ...extra,
  });
};

export const isSandboxUnavailableError = (error: unknown): boolean =>
  normalizeBlaxelSandboxError(error).isSandboxUnavailableError;

// Must match MAX_FUNCTIONS_VERSIONS in the sandbox template's
// build-functions.ts, which prunes the catalog to this many generations.
export const SANDBOX_FUNCTIONS_VERSIONS = 10;

const isSnapshotUnavailableError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'SnapshotUnavailableError';

/**
 * Engine driving the remote Blaxel sandbox lifecycle for AICC.
 *
 * User edits and build-time side effects collect file changes in closures,
 * then drain them through `remoteFileSystemFilesCommitted` on the next IDLE
 * tick. AI tool writes dispatch that action directly.
 */
export const RemoteSandboxEngine = (
  dispatch: Dispatch<RemoteSandboxActions>
) => {
  let committedFileContents = new Map<string, string>();
  let deletedFiles = new Set<string>();
  // EXT-865: paths this engine instance wrote via writeTree, so the divergence
  // probe can tell a vanished engine write (cross-tab clobber, sandbox GC) apart
  // from a file the store thinks is SYNCED but this engine never wrote (the AI
  // tools' direct `client.fs.write`). Reset on expiry — a fresh sandbox has none.
  const enginePersistedPaths = new Set<string>();
  // EXT-865: per-tab designer session id (window.name-backed, shared with
  // multiplayer presence and the X-Session-Id header). Two Designer tabs of the
  // same user share one sandbox but run separate, unserialized chains; tagging
  // divergence events with it lets Datadog tell the tabs apart.
  const tabInstanceId = getDesignerSessionId();
  // EXT-865: serialize sandbox FS mutations (each rm/writeTree + its `ls` verify)
  // through one FIFO promise, so overlapping engine ticks can't race an `rm` and a
  // re-creating `writeTree` on the same path (a delete→fast-undo could lose a file).
  let fsMutationChain: Promise<unknown> = Promise.resolve();
  const serializeFsMutation = <T>(operation: () => Promise<T>): Promise<T> => {
    // Run once the previous op settles; the caller gets this op's real outcome.
    const run = fsMutationChain.then(operation);
    // Advance the chain tail, swallowing failures so one rejected op doesn't
    // leave the tail rejected — which would skip every later op.
    fsMutationChain = run.catch(() => undefined);
    return run;
  };
  // The first build in a sandbox pays cold install cost; reset on expiry.
  let hasCompletedBuild = false;
  // Functions generations whose bundles exist in the current sandbox's
  // catalog. A cached hash outside this set would preview the newest draft.
  const sandboxFunctionsHashes = new Set<string>();
  let observedSandbox: CreateSandboxResponse | undefined;
  // EXT-297: successful build artifacts keyed by build-inputs hash, so
  // undo/redo back to already-built source skips the Blaxel round trip.
  // Deliberately NOT reset on expiry — artifacts are final outputs,
  // independent of any particular sandbox instance. Entries are stored
  // gzip-compressed and the budget bounds cumulative *compressed* bytes; dev
  // bundles are large and highly repetitive (they inline the React dev build),
  // so compression lets this budget hold many more undo/redo versions.
  const buildArtifactCache = createBuildArtifactCache(24 * 1024 * 1024);

  return async ({
    sandboxState,
    siteShortName,
  }: {
    sandboxState: RemoteSandboxState;
    siteShortName: string;
  }) => {
    // Don't do anything without code components
    if (
      siteShortName === '' ||
      Object.keys(sandboxState.filesystem).length === 0
    )
      return;
    if (sandboxState.sandbox.status === 'INIT') {
      dispatch(remoteSandboxInitiated());
      const session = await getSandboxSession(siteShortName);
      if (session) {
        dispatch(remoteSandboxCreated(session));
      }
      return;
    }
    if (sandboxState.sandbox.status === 'CREATED') {
      // Every recreate path lands here with a new session object, including
      // expiries dispatched outside this engine.
      if (sandboxState.sandbox.value !== observedSandbox) {
        observedSandbox = sandboxState.sandbox.value;
        sandboxFunctionsHashes.clear();
      }
      if (isSessionExpired(sandboxState.sandbox.value.session)) {
        // Reset per-session state so the next sandbox starts clean:
        // - `hasCompletedBuild` so the first build after re-create is
        //   tagged `isFirstBuild: true`.
        // - `committedFileContents` and `deletedFiles` so any entries
        //   captured during this expired session don't dispatch into
        //   the freshly recreated sandbox on the next IDLE tick (which
        //   would mark files SYNCED that were never written remotely).
        // - `enginePersistedPaths` so divergence attribution starts fresh
        //   against the new sandbox (nothing is engine-written on it yet).
        hasCompletedBuild = false;
        committedFileContents = new Map<string, string>();
        deletedFiles = new Set<string>();
        enginePersistedPaths.clear();
        dispatch(remoteSandboxExpired());
        return;
      }

      const sandboxClient = await getSandboxClientFromSession(
        sandboxState.sandbox.value.session
      );

      // Reconcile deletions before syncing/building: remove from the sandbox
      // any component file the store marked PENDING_DELETE (its component was
      // deleted in the Designer). Done before the build so a deleted component
      // that failed to compile no longer blocks the rebuild. The PENDING_DELETE
      // marker lives in the store, so a transient failure is simply retried on
      // a later engine run; a non-recoverable error (most commonly "already gone")
      // is treated as attempted to avoid stalling every build behind an
      // un-deletable file.
      const pendingDeletePaths = filterMap(
        Object.entries(sandboxState.filesystem),
        ([path, file]) => (file.status === 'PENDING_DELETE' ? path : undefined)
      );
      if (pendingDeletePaths.length > 0) {
        // Content buffered for a path the store already dropped is stale, and
        // draining it would mint the file back into the tree (EXT-181).
        pendingDeletePaths.forEach((path) =>
          committedFileContents.delete(stripLeadingSlash(path))
        );
        const attemptedPaths: string[] = [];
        let sandboxUnavailable = false;
        // EXT-865: run the rm batch and its verify ls as one serialized unit
        // so a concurrent writeTree for the same path can't overlap it.
        const removedPaths = await serializeFsMutation(async () => {
          await Promise.all(
            pendingDeletePaths.map(async (path) => {
              try {
                await sandboxClient.fs.rm(`${SANDBOX_WORKING_DIR}${path}`);
                attemptedPaths.push(path);
              } catch (error) {
                reportBlaxelSandboxError(
                  error,
                  'remoteSandboxEngine.removeFile'
                );
                if (isSandboxUnavailableError(error)) {
                  sandboxUnavailable = true;
                } else {
                  attemptedPaths.push(path);
                }
              }
            })
          );

          // The rm resolving doesn't prove the file is gone, so verify before
          // acknowledging: only drop from state the paths the verify confirmed
          // absent. Any still present stays PENDING_DELETE and is retried on a
          // later engine run. Kept inside the serialized unit so nothing may
          // write to a path until its removal is confirmed. Skipped when the
          // sandbox is gone — it will be recreated.
          return sandboxUnavailable || attemptedPaths.length === 0
            ? []
            : verifyRemovedPaths(sandboxClient, attemptedPaths);
        });

        // The whole sandbox is gone — recreate it. The reset drops every
        // PENDING_DELETE entry, and the fresh sandbox is rehydrated from the
        // projection (which excludes the deleted components), so there is
        // nothing to verify or acknowledge here.
        if (sandboxUnavailable) {
          return dispatch(remoteSandboxExpired());
        }

        if (attemptedPaths.length > 0) {
          if (removedPaths.length > 0) {
            dispatch(remoteSandboxFilesDeleted(removedPaths));
            // Drop acknowledged removals so a later AI re-create of the same
            // path isn't misattributed as engine-written (EXT-865).
            removedPaths.forEach((path) => enginePersistedPaths.delete(path));
          }
          if (removedPaths.length !== attemptedPaths.length) {
            const removedSet = new Set(removedPaths);
            const unverifiedPaths = attemptedPaths
              .filter((path) => !removedSet.has(path))
              .slice(0, 10);
            console.warn(
              '[RemoteSandboxEngine] Unable to verify all sandbox file removals',
              {
                requestedRemovalCount: attemptedPaths.length,
                verifiedRemovalCount: removedPaths.length,
                unverifiedPaths,
              }
            );
            telemetry.sendEvent(
              'datadog::vccSandboxDeleteVerificationMismatch',
              {
                requestedRemovalCount: attemptedPaths.length,
                verifiedRemovalCount: removedPaths.length,
                unverifiedPaths,
              }
            );
          }
        }
      }

      const filesToSync = filterMap(
        Object.entries(sandboxState.filesystem),
        ([name, file]) => {
          if (file.status === 'PENDING') {
            return {
              path: `${name}`,
              content: file.source,
            };
          }
        }
      );

      if (filesToSync.length > 0) {
        dispatch(remoteSandboxSyncStarted(filesToSync.map((f) => f.path)));

        try {
          // EXT-865: run the writeTree and its verify ls as one serialized unit
          // so it can't overlap an in-flight rm for the same path.
          const syncedPaths = await serializeFsMutation(async () => {
            const writeResponse = await sandboxClient.fs.writeTree(
              filesToSync,
              SANDBOX_WORKING_DIR
            );
            return verifySyncedPaths(sandboxClient, filesToSync, writeResponse);
          });
          for (const {path, content} of filesToSync) {
            const relativePath = stripLeadingSlash(path);
            committedFileContents.set(relativePath, content);
            deletedFiles.delete(relativePath);
            // Record that this engine issued a write for the path, for the
            // divergence probe's attribution (EXT-865).
            enginePersistedPaths.add(path);
          }
          dispatch(remoteSandboxSyncCompleted(filesToSync.map((f) => f.path)));
          if (syncedPaths.length !== filesToSync.length) {
            const syncedPathSet = new Set(syncedPaths);
            const unverifiedFilePaths = filesToSync
              .map((file) => file.path)
              .filter((path) => !syncedPathSet.has(path))
              .slice(0, 10);
            console.warn(
              '[RemoteSandboxEngine] Unable to verify all sandbox file writes',
              {
                requestedFileCount: filesToSync.length,
                verifiedFileCount: syncedPaths.length,
                unverifiedFilePaths,
              }
            );
            telemetry.sendEvent('datadog::vccSandboxSyncVerificationMismatch', {
              requestedFileCount: filesToSync.length,
              verifiedFileCount: syncedPaths.length,
              unverifiedFilePaths,
            });
          }
        } catch (error) {
          reportBlaxelSandboxError(error, 'remoteSandboxEngine.writeTree');
          if (isSandboxUnavailableError(error)) {
            return dispatch(remoteSandboxExpired());
          }
          throw error;
        }
      }

      if (
        allSynced(sandboxState.filesystem) &&
        sandboxState.job.status === 'QUEUED'
      ) {
        // Stamp source locations into the dev-preview build only when the
        // build-time-stamping flag is on (EXT-897), so the deterministic
        // apply can map an edit to its exact source spot. Gated separately
        // from VCC_INSTANT_TEXT_APPLY so the stamped sandbox image can be
        // rolled out and verified independently. Read once per build.
        const stampSourceLoc = getFeatureFlagOutsideOfReact(
          FEATURE_FLAGS.VCC_BUILD_TIME_STAMPING
        );
        const codeFunctions = getFeatureFlagOutsideOfReact(
          FEATURE_FLAGS.CODE_FUNCTIONS
        );

        // EXT-297: files are already synced to the sandbox at this point, so
        // when the projected filesystem matches a previous successful build
        // (the undo/redo case) we can skip the build round trip and re-emit
        // the cached artifact. Same action sequence as a real build keeps the
        // job state machine, buildVersion, and canvas evaluation flow intact.
        // codeFunctions is keyed because cached artifacts carry `functionsHash`.
        const buildInputsHash = `${computeBuildInputsHash(
          sandboxState.filesystem
        )}:stampSourceLoc=${stampSourceLoc}:codeFunctions=${codeFunctions}`;
        const cachedArtifact = buildArtifactCache.get(buildInputsHash);

        // EXT-2212: sourceRevisions lets CODE_EVALUATED discard metadata from a
        // build that raced an edit or undo. Flag-off skips the hashing and
        // omits the key, which leaves the reducer's staleness guard inert.
        const aiccMetadataEdits = getFeatureFlagOutsideOfReact(
          FEATURE_FLAGS.AICC_METADATA_EDITS
        );
        const sourceRevisions: Record<string, string> = {};
        if (aiccMetadataEdits) {
          for (const [path, file] of Object.entries(sandboxState.filesystem)) {
            const componentId = fromComponentFilename(path);
            if (componentId && file.status !== 'PENDING_DELETE') {
              sourceRevisions[componentId] = hashProjectedSourceForStamp(
                file.source
              );
            }
          }
        }
        // Consumed before the cache-hit branch so a harness write whose inputs
        // hash is already cached reports the marker on the hit instead of
        // leaking it into the next unrelated build.
        const pasteHarnessApplied = consumePasteHarnessApplied();
        // The server dropped this generation while the mirror still lists it
        // (a failed component pass shifts the catalog unseen); the build below
        // re-emits it.
        if (sandboxState.evictedFunctionsHash) {
          sandboxFunctionsHashes.delete(sandboxState.evictedFunctionsHash);
        }
        // A hit replays functionsHash, which only this sandbox's catalog can
        // serve; a hash from an earlier sandbox falls through to a real build.
        const cacheSkipReason =
          cachedArtifact?.functionsHash &&
          !sandboxFunctionsHashes.has(cachedArtifact.functionsHash)
            ? 'functionsHashNotInSandbox'
            : null;
        if (cachedArtifact && !cacheSkipReason) {
          dispatch(codeBuildStarted());
          dispatch(
            codeBuildSucceeded({
              ...cachedArtifact,
              ...(aiccMetadataEdits ? {sourceRevisions} : {}),
            })
          );
          telemetry.sendEvent('datadog::vccBuildCacheHit', {
            fileCount: Object.keys(sandboxState.filesystem).length,
            pasteHarnessApplied,
          });
          return;
        }

        dispatch(codeBuildStarted());

        // EXT-865: observe-only divergence check before a real build — cache
        // hits return above without consuming sandbox state, so a stale sandbox
        // can't affect them. Runs through the FS-mutation chain so the ls sees
        // this tab's settled writes.
        const divergentPaths = await serializeFsMutation(() =>
          detectDivergentSyncedPaths(sandboxClient, sandboxState.filesystem)
        );
        if (divergentPaths.length > 0) {
          // Split missing paths by attribution for the EXT-865 escalation
          // playbook: vanished engine writes (see enginePersistedPaths) vs. a
          // file an external writer never routed through this chain.
          const missingWrittenByEngineCount = divergentPaths.filter((path) =>
            enginePersistedPaths.has(path)
          ).length;
          telemetry.sendEvent('datadog::vccSandboxDivergenceDetected', {
            missingFileCount: divergentPaths.length,
            missingPaths: divergentPaths.slice(0, 10),
            entryModuleMissing: divergentPaths.includes(
              PROJECTED_INDEX_FILENAME
            ),
            missingWrittenByEngineCount,
            missingNotWrittenByEngineCount:
              divergentPaths.length - missingWrittenByEngineCount,
            tabInstanceId,
          });
        }

        const isFirstBuild = !hasCompletedBuild;
        const buildStart = performance.now();
        // Client wall-clock timings inflate when the tab is backgrounded.
        const readTabFocus = createTabFocusObserver();
        let tabFocusRead = false;
        const consumeTabFocus = (): boolean => {
          if (tabFocusRead) return false;
          tabFocusRead = true;
          return readTabFocus();
        };

        try {
          const {
            result: {result, meta},
            committed,
            deleted,
            trackingMode,
            startBarrierMs,
            startMarkerWritten,
            endBarrierMs,
          } = await withFileChangeTracking(sandboxClient, (sandbox) =>
            buildCodeLibrary(sandbox, {stampSourceLoc, codeFunctions})
          );

          for (const [path, content] of Object.entries(committed)) {
            committedFileContents.set(path, content);
            deletedFiles.delete(path);
          }
          for (const path of deleted) {
            deletedFiles.add(path);
            committedFileContents.delete(path);
          }

          Result.when(result, {
            Ok: (details) => {
              // Dispatch (which drives the canvas re-render) before populating
              // the cache, so the synchronous gzip in `set` never delays the
              // render. Cache successes only — failed builds carry no artifact
              // and a retry should always re-run for fresh diagnostics.
              dispatch(
                codeBuildSucceeded({
                  ...details,
                  ...(aiccMetadataEdits ? {sourceRevisions} : {}),
                })
              );
              if (details.functionsHash) {
                // Re-insert so iteration order tracks the catalog's
                // most-recently-built order; evict what its prune dropped.
                sandboxFunctionsHashes.delete(details.functionsHash);
                sandboxFunctionsHashes.add(details.functionsHash);
                const [oldest] = sandboxFunctionsHashes;
                if (
                  oldest &&
                  sandboxFunctionsHashes.size > SANDBOX_FUNCTIONS_VERSIONS
                ) {
                  sandboxFunctionsHashes.delete(oldest);
                }
              }
              buildArtifactCache.set(buildInputsHash, {
                js: details.js,
                css: details.css,
                functionsHash: details.functionsHash,
              });
            },
            Error: (diagnostics) => dispatch(codeBuildFailed(diagnostics)),
          });
          dispatch(
            codeDependenciesInstallationFinished({
              recoveredNames: meta.depsInstall.recoveredNames.slice(),
            })
          );

          hasCompletedBuild = true;
          // APAI-3268: cap package-name lists to keep telemetry bounded.
          const rejectedNames = meta.depsInstall.rejectedNames.slice(0, 10);
          const recoveredNames = meta.depsInstall.recoveredNames.slice(0, 10);
          const {
            serverBuildDurationMs,
            serverTimingComplete,
            serverTimingSuspect,
            serverTimingSuspectPhases,
          } = deriveServerBuildDuration(meta);
          telemetry.sendEvent('datadog::vccBuildDuration', {
            value: performance.now() - buildStart,
            status: result.tag === 'Ok' ? 'success' : 'failure',
            failurePhase: meta.failurePhase,
            isFirstBuild,
            cacheSkipReason,
            pasteHarnessApplied,
            depsInstallMs: meta.phaseDurations.deps_install,
            typecheckMs: meta.phaseDurations.typecheck,
            viteBuildMs: meta.phaseDurations.vite_build,
            fsReadMs: meta.phaseDurations.fs_read,
            depsInstallRejectedNames: rejectedNames,
            depsInstallRecoveredNames: recoveredNames,
            depsInstallRetryHappened: meta.depsInstall.retryHappened,
            depsInstallRetryRecovered: meta.depsInstall.retryRecovered,
            depsInstallCacheRepairHappened:
              meta.depsInstall.cacheRepairHappened,
            depsInstallCacheRepairRecovered:
              meta.depsInstall.cacheRepairRecovered,
            buildTrackingMode: trackingMode,
            startBarrierMs,
            startMarkerWritten,
            endBarrierMs,
            hasMainTabBeenUnfocused: consumeTabFocus(),
            serverBuildDurationMs,
            serverDepsInstallMs: meta.serverPhaseDurations.deps_install,
            serverTypecheckMs: meta.serverPhaseDurations.typecheck,
            serverViteBuildMs: meta.serverPhaseDurations.vite_build,
            serverTimingComplete,
            serverTimingSuspect,
            serverTimingSuspectPhases,
          });
          for (const name of rejectedNames) {
            telemetry.sendEvent('datadog::vccBadPackageName', {
              name,
              source: 'scan-imports',
            });
          }
          for (const name of recoveredNames) {
            telemetry.sendEvent('datadog::vccBadPackageName', {
              name,
              source: 'bun-404',
            });
          }
        } catch (error) {
          if (isSnapshotUnavailableError(error)) {
            telemetry.sendEvent('datadog::vccBuildDuration', {
              value: performance.now() - buildStart,
              status: 'snapshot_unavailable',
              failurePhase: 'snapshot_setup',
              isFirstBuild,
              cacheSkipReason,
              pasteHarnessApplied,
              hasMainTabBeenUnfocused: consumeTabFocus(),
              serverBuildDurationMs: null,
              serverTimingComplete: false,
              serverTimingSuspect: false,
              serverTimingSuspectPhases: [],
            });
            return dispatch(
              codeBuildFailed(
                'Build tracking temporarily unavailable. Please retry.'
              )
            );
          }
          telemetry.sendEvent('datadog::vccBuildDuration', {
            value: performance.now() - buildStart,
            status: 'exception',
            // 'unknown' (not 'none') because the build threw before we could
            // attribute the failure to a phase. 'none' is reserved for the
            // success path so dashboards can distinguish unattributed
            // exceptions from genuine no-failure builds.
            failurePhase: 'unknown',
            isFirstBuild,
            cacheSkipReason,
            pasteHarnessApplied,
            hasMainTabBeenUnfocused: consumeTabFocus(),
            serverBuildDurationMs: null,
            serverTimingComplete: false,
            serverTimingSuspect: false,
            serverTimingSuspectPhases: [],
          });
          reportBlaxelSandboxError(
            error,
            'remoteSandboxEngine.buildCodeLibrary'
          );
          if (isSandboxUnavailableError(error)) {
            return dispatch(remoteSandboxExpired());
          }
          // Any other failure still has to settle the job, or the
          // rebuilding-lock selectors (selectIsSandboxRebuilding /
          // canManageFiles) never see `job` leave RUNNING and stay locked
          // for the rest of the session.
          dispatch(codeBuildFailed('Build failed unexpectedly. Please retry.'));
          throw error;
        } finally {
          if (!tabFocusRead) readTabFocus();
        }
      }

      if (
        sandboxState.job.status === 'IDLE' &&
        allSynced(sandboxState.filesystem)
      ) {
        if (committedFileContents.size === 0 && deletedFiles.size === 0) {
          return;
        }

        const committed = Object.fromEntries(committedFileContents);
        const filesToDelete = [...deletedFiles];
        committedFileContents = new Map<string, string>();
        deletedFiles = new Set<string>();

        dispatch(
          remoteFileSystemFilesCommitted({
            committed,
            deleted: filesToDelete,
          })
        );
      }
    }
  };
};

export const remoteSandboxEngineConfig = engineConfig(
  RemoteSandboxEngine,
  selectRemoteSandbox
);
